import React from "react";

import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { t } from "@auto/services";

import styles from "@auto/users/__BackofficeUserProfile.module.styl";

const SYSTEM_ADMINISTRATOR = "SYSTEM_ADMINISTRATOR";

const ROLE_SET_TRANSLATIONS = Object.freeze({
  SYSTEM_ADMINISTRATOR: {
    label: "role.systemAdministrator",
    description: "role.systemAdministrator.description"
  },
  USER_ADMINISTRATION: {
    label: "role.userAdministration",
    description: "role.userAdministration.description"
  },
  STATISTICAL_REPORTS: {
    label: "role.statisticalReports",
    description: "role.statisticalReports.description"
  },
  OPERATIONAL_REPORTS: {
    label: "role.operationalReports",
    description: "role.operationalReports.description"
  },
  OPERATOR_MANAGEMENT: {
    label: "role.operatorManagement",
    description: "role.operatorManagement.description"
  },
  VOTER_REGISTRATION_MANAGEMENT: {
    label: "role.voterRegistrationManagement",
    description: "role.voterRegistrationManagement.description"
  },
  ELECTORAL_GEOGRAPHY_MANAGEMENT: {
    label: "role.electoralGeographyManagement",
    description: "role.electoralGeographyManagement.description"
  },
  DIAGNOSTICS_MANAGEMENT: {
    label: "role.diagnosticsManagement",
    description: "role.diagnosticsManagement.description"
  }
});

const SPECIAL_PERMISSION_WORDS = Object.freeze({
  API: "API",
  GIS: "GIS",
  ID: "ID",
  JWT: "JWT",
  MDM: "MDM",
  NINU: "NINU",
  ONI: "ONI",
  BACKOFFICE: "Qalaa"
});

export const permissionNames = (roleSet) => {
  return (roleSet?.permissions || [])
    .map((permission) => permission?.name)
    .filter(Boolean);
};

export const selectedPermissionNames = (profile) => {
  const selected = new Set();

  for (const roleSet of profile?.roleSets || []) {
    for (const permission of roleSet?.permissions || []) {
      if (permission?.selected === true && permission?.name) {
        selected.add(permission.name);
      }
    }
  }

  return selected;
};

export const samePermissionSelection = (left, right) => {
  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
};

const roleSetLabel = (roleSet) => {
  const translation = ROLE_SET_TRANSLATIONS[roleSet?.id];

  return translation
    ? t(translation.label)
    : roleSet?.label || roleSet?.id || "";
};

const roleSetDescription = (roleSet) => {
  const translation = ROLE_SET_TRANSLATIONS[roleSet?.id];

  return translation
    ? t(translation.description)
    : roleSet?.description || "";
};

const permissionLabel = (permissionName) => {
  const words = String(permissionName || "")
    .split("_")
    .filter(Boolean)
    .map((word) => SPECIAL_PERMISSION_WORDS[word] || word.toLowerCase());

  if (words.length === 0) {
    return "";
  }

  const [first, ...rest] = words;
  const firstWord = Object.values(SPECIAL_PERMISSION_WORDS).includes(first)
    ? first
    : first.charAt(0).toUpperCase() + first.slice(1);

  return [firstWord, ...rest].join(" ");
};

const RolePermissionSelector = ({
  roleSets,
  selectedPermissions,
  saving,
  onToggleRole,
  onTogglePermission
}) => {
  const [expandedRoles, setExpandedRoles] = React.useState(new Set());
  const administratorSelected = selectedPermissions.has("ALL");

  const toggleExpanded = (roleId) => {
    setExpandedRoles((current) => {
      const next = new Set(current);

      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }

      return next;
    });
  };

  return (
    <div className={styles.roleList}>
      {(roleSets || []).map((roleSet) => {
        const names = permissionNames(roleSet);
        const selectedCount = names.filter(
          (name) => selectedPermissions.has(name)
        ).length;
        const selected = names.length > 0 && selectedCount === names.length;
        const partial = selectedCount > 0 && !selected;
        const expandable =
          roleSet.id !== SYSTEM_ADMINISTRATOR
          && !names.includes("ALL")
          && names.length > 1;
        const expanded = expandable && expandedRoles.has(roleSet.id);
        const disabled =
          saving
          || (administratorSelected && roleSet.id !== SYSTEM_ADMINISTRATOR);

        return (
          <div
            key={roleSet.id}
            className={[
              styles.roleCard,
              selected ? styles.roleCardSelected : "",
              partial ? styles.roleCardPartial : ""
            ].filter(Boolean).join(" ")}
          >
            <div className={styles.roleHeader}>
              <Checkbox
                checked={selected}
                indeterminate={partial}
                disabled={disabled}
                onChange={() => onToggleRole(roleSet)}
              />

              <button
                type="button"
                className={styles.roleInfo}
                disabled={!expandable}
                onClick={() => expandable && toggleExpanded(roleSet.id)}
              >
                <span className={styles.roleName}>
                  {roleSetLabel(roleSet)}
                </span>

                <span className={styles.roleDescription}>
                  {roleSetDescription(roleSet)}
                </span>
              </button>

              {expandable ? (
                <span className={styles.roleCount}>
                  {selectedCount}/{names.length}
                </span>
              ) : null}

              {expandable ? (
                <IconButton
                  size="small"
                  className={expanded ? styles.expandButtonOpen : styles.expandButton}
                  onClick={() => toggleExpanded(roleSet.id)}
                  aria-expanded={expanded}
                >
                  <ExpandMoreIcon fontSize="small" />
                </IconButton>
              ) : null}
            </div>

            {expandable ? (
              <Collapse in={expanded} timeout="auto" unmountOnExit>
                <div className={styles.permissionPanel}>
                  <div className={styles.permissionGrid}>
                    {(roleSet.permissions || []).map((permission) => (
                      <label
                        key={permission.name}
                        className={styles.permissionRow}
                        title={permission.name}
                      >
                        <Checkbox
                          size="small"
                          checked={selectedPermissions.has(permission.name)}
                          disabled={saving || administratorSelected}
                          onChange={() => onTogglePermission(permission.name)}
                        />

                        <span className={styles.permissionLabel}>
                          {permissionLabel(permission.name)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </Collapse>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default RolePermissionSelector;
